import { ENGINE_KIND, SpeakOptions, SpeechEngine } from './speechEngine'

const DEFAULT_LANG = 'en-US'
const DEFAULT_RATE = 0.95
const WORD_BOUNDARY = 'word'
const CANCELLATION_ERRORS = ['canceled', 'interrupted']
const SPEAK_FAILED = 'speak-failed'

let currentUtterance: SpeechSynthesisUtterance | null = null

const isSupported = () => typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window

const primaryLanguage = (lang: string) => lang.toLowerCase().split('-')[0]

const localVoiceFor = (lang: string) => {
  const language = primaryLanguage(lang)
  const voices = window.speechSynthesis.getVoices() || []
  return voices.find(voice => voice.localService && voice.lang && primaryLanguage(voice.lang) === language) || null
}

const speak = (text: string, options: SpeakOptions): object | null => {
  if (!isSupported()) { return null }

  const { onBoundary, onEnd, onError, onStart, rate, startCharIndex = 0, } = options
  const lang = options.lang || DEFAULT_LANG
  const offset = Math.max(0, Math.min(startCharIndex, text.length))
  const utterance = new SpeechSynthesisUtterance(text.slice(offset))
  utterance.lang = lang
  utterance.rate = rate ?? DEFAULT_RATE
  const voice = localVoiceFor(lang)
  if (voice) { utterance.voice = voice }

  const finish = () => {
    if (currentUtterance === utterance) { currentUtterance = null }
    if (onEnd) { onEnd() }
  }
  utterance.onstart = () => {
    if (onStart) { onStart() }
  }
  utterance.onend = finish
  utterance.onerror = (e: SpeechSynthesisErrorEvent) => {
    const reason = e?.error || 'unknown'
    if (onError && !CANCELLATION_ERRORS.includes(reason)) { onError(reason) }
    finish()
  }
  if (onBoundary) {
    utterance.onboundary = (e: SpeechSynthesisEvent) => {
      if (e.name && e.name !== WORD_BOUNDARY) { return }
      onBoundary(e.charIndex + offset)
    }
  }

  currentUtterance = utterance
  try {
    window.speechSynthesis.speak(utterance)
  } catch (error) {
    if (onError) { onError(SPEAK_FAILED) }
    finish()
    return null
  }
  return utterance
}

const cancel = () => {
  if (!isSupported()) { return }

  currentUtterance = null
  window.speechSynthesis.cancel()
}

const unsupportedControl = () => false

export const webSpeechEngine: SpeechEngine = {
  cancel,
  isSupported,
  kind: ENGINE_KIND.WEB_SPEECH,
  pause: unsupportedControl,
  resume: unsupportedControl,
  seekToChar: unsupportedControl,
  setRate: unsupportedControl,
  speak,
}
